import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Table, Empty, Tag, Input, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faX } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFormAction, updateProtocol } from "../../../redux/protocols/protocolsSlice";
import {
  useLazyGetProtocolsQuery,
  usePostProtocolMutation,
  usePutProtocolMutation,
} from "../../../redux/protocols/protocolAPI";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import ProtocolTableActions from "./ProtocolTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { ProtocolInterface } from "../../../interfaces/Protocols.interface";
// CONSTANTS
import { BLANK_PROTOCOL } from "../../../constants/protocol";
import { FormAction } from "../../../types/enums";

function ProtocolTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const { Column, ColumnGroup } = Table;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [data, setData] = useState<ProtocolInterface[]>([]);
  const [protocol, setProtocol] = useState<ProtocolInterface>({
    ...BLANK_PROTOCOL,
  });
  const [errorMessage, setErrorMessage] = useState<any>({});
  const [totalRows, setTotalRows] = useState<number>(0);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetProtocols,
    {
      data: dataGetProtocols,
      isLoading: isLoadingGetProtocols,
      isFetching: isFetchingGetProtocols,
      isError: isErrorGetProtocols,
      error: errorGetProtocols,
    },
  ] = useLazyGetProtocolsQuery();

  const [
    triggerPostProtocol,
    {
      isSuccess: isSuccessPostProtocol,
      isLoading: isLoadingPostProtocol,
      isError: isErrorPostProtocol,
      error: errorPostProtocol,
      reset: resetPostProtocols,
    },
  ] = usePostProtocolMutation();

  const [
    triggerPutProtocol,
    {
      isSuccess: isSuccessPutProtocol,
      isLoading: isLoadingPutProtocol,
      isError: isErrorPutProtocol,
      error: errorPutProtocol,
      reset: resetPutProtocols,
    },
  ] = usePutProtocolMutation();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const {
    refreshTable,
    formAction,
    protocol: selectedProtocol,
  } = useSelector((state: RootState) => state.protocol);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getInitialData = async () => {
    const token = await GETJwtToken();
    triggerGetProtocols({
      token,
    });
  };

  const onClickRefreshTable = async () => {
    getInitialData();
  };

  const onChangeFormValueName = (newValue: string) => {
    setProtocol((prev: ProtocolInterface) => ({ ...prev, name: newValue }));
  };

  const onChangeFormValueInterface = (newValue: string, variableName: string) => {
    const copy: ProtocolInterface = structuredClone(protocol);
    copy[variableName] = JSON.parse(newValue);
    setProtocol(copy);
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    if (formAction === FormAction.Create) {
      const BODY = {
        token,
        body: {
          name: protocol.name,
          default_interface: JSON.stringify(protocol.defaultInterface),
          cdo_interface: JSON.stringify(protocol.cdoInterface),
        },
      };
      triggerPostProtocol(BODY);
    } else {
      const BODY = {
        token,
        id: protocol.id,
        body: {
          name: protocol.name,
          default_interface: JSON.stringify(protocol.defaultInterface),
          cdo_interface: JSON.stringify(protocol.cdoInterface),
        },
      };
      triggerPutProtocol(BODY);
    }
  };

  const resetForm = () => {
    resetPostProtocols();
    resetPutProtocols();
    setProtocol({ ...BLANK_PROTOCOL });
    setErrorMessage({});
    dispatch(updateFormAction(FormAction.Create));
    dispatch(updateProtocol({ ...BLANK_PROTOCOL }));
  };

  const checkFormIsValid = () => {
    let formIsValid = true;
    if (protocol.name === "") {
      formIsValid = false;
    }
    return formIsValid;
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (refreshTable) {
      onClickRefreshTable();
    }
  }, [refreshTable]);

  useEffect(() => {
    if (dataGetProtocols && dataGetProtocols.data) {
      setData(dataGetProtocols.data);
      setTotalRows(dataGetProtocols.total);
    }
  }, [dataGetProtocols]);

  useEffect(() => {
    if (errorGetProtocols) {
      setErrorMessage(errorGetProtocols);
      setData([]);
    }
  }, [errorGetProtocols]);

  useEffect(() => {
    resetForm();
  }, [isSuccessPostProtocol, isSuccessPutProtocol, isErrorPostProtocol, isErrorPutProtocol]);

  useEffect(() => {
    if (formAction === FormAction.Create) {
      setProtocol({ ...BLANK_PROTOCOL });
    }
  }, [formAction]);

  useEffect(() => {
    setProtocol(selectedProtocol);
  }, [selectedProtocol]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div>
        {hasPermission(Permission.ProtocolView, permissionSet ?? []) ? (
          <Table
            locale={{
              emptyText: (
                <TableNoDataBox
                  isError={isErrorGetProtocols}
                  errorMessage={
                    errorMessage && errorMessage.data && errorMessage.data.message
                      ? errorMessage.data.message
                      : "Error"
                  }
                  noItemsFound="No actions found"
                />
              ),
            }}
            rowClassName={(record, index) => getTableRowClass(index, theme)}
            dataSource={data}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
            }}
            size="small"
            className="mt-3"
            loading={
              isFetchingGetProtocols ||
              isLoadingGetProtocols ||
              isLoadingPostProtocol ||
              isLoadingPutProtocol
            }
            footer={() => (
              <div className="generalStyles__flexEnd">
                <Tag>{totalRows} Rows</Tag>
              </div>
            )}
          >
            {/** NAME */}
            <ColumnGroup title="Name">
              <Column
                width={200}
                title={
                  <Input
                    size="small"
                    placeholder="Name"
                    value={formAction === FormAction.Create ? protocol.name : ""}
                    disabled={formAction === FormAction.Edit}
                    onChange={(event: React.FormEvent<HTMLInputElement>) => {
                      onChangeFormValueName(event.currentTarget.value);
                    }}
                  />
                }
                dataIndex="name"
                key="name"
                render={(text, record: ProtocolInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === protocol.id ? (
                      <Input
                        size="small"
                        placeholder="Name"
                        value={protocol.name}
                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                          onChangeFormValueName(event.currentTarget.value);
                        }}
                      />
                    ) : (
                      <span>{text}</span>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** DEFAULT INTERFACE */}
            <ColumnGroup title="Default Interface">
              <Column
                width={200}
                title={
                  <Input
                    size="small"
                    placeholder="Default Interface"
                    value={
                      formAction === FormAction.Create
                        ? JSON.stringify(protocol.defaultInterface)
                        : ""
                    }
                    disabled={formAction === FormAction.Edit}
                    onChange={(event: React.FormEvent<HTMLInputElement>) => {
                      onChangeFormValueInterface(event.currentTarget.value, "defaultInterface");
                    }}
                  />
                }
                dataIndex="defaultInterface"
                key="defaultInterface"
                render={(text, record: ProtocolInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === protocol.id ? (
                      <Input
                        size="small"
                        placeholder="Default Interface"
                        value={JSON.stringify(protocol.defaultInterface)}
                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                          onChangeFormValueInterface(event.currentTarget.value, "defaultInterface");
                        }}
                      />
                    ) : (
                      <span>{JSON.stringify(text)}</span>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** CDO */}
            <ColumnGroup title="CDO">
              <Column
                width={200}
                title={
                  <Input
                    size="small"
                    placeholder="CDO"
                    value={
                      formAction === FormAction.Create ? JSON.stringify(protocol.cdoInterface) : ""
                    }
                    disabled={formAction === FormAction.Edit}
                    onChange={(event: React.FormEvent<HTMLInputElement>) => {
                      onChangeFormValueInterface(event.currentTarget.value, "cdoInterface");
                    }}
                  />
                }
                dataIndex="cdoInterface"
                key="cdoInterface"
                render={(text, record: ProtocolInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === protocol.id ? (
                      <Input
                        size="small"
                        placeholder="CDO"
                        value={JSON.stringify(protocol.cdoInterface)}
                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                          onChangeFormValueInterface(event.currentTarget.value, "cdoInterface");
                        }}
                      />
                    ) : (
                      <span>{JSON.stringify(text)}</span>
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
            {/** ACTIONS */}
            <ColumnGroup align="center" title={t("edgeManagement.banks.actions")} width={200}>
              <Column
                align="center"
                width={200}
                title={
                  <div className="generalStyles__flexCenter">
                    <div>
                      <Tooltip title={t("general.save")}>
                        <Button
                          className={
                            formAction === FormAction.Create && checkFormIsValid()
                              ? "buttonStyle__10"
                              : ""
                          }
                          disabled={!checkFormIsValid() || formAction === FormAction.Edit}
                          onClick={onClickSave}
                          icon={<FontAwesomeIcon icon={faSave} />}
                        />
                      </Tooltip>
                    </div>
                    <div className="generalStyles__mlFix">
                      <Tooltip title={t("general.clear")}>
                        <Button
                          className={formAction === FormAction.Create ? "buttonStyle__14" : ""}
                          onClick={resetForm}
                          disabled={formAction === FormAction.Edit}
                          icon={<FontAwesomeIcon icon={faX} />}
                        />
                      </Tooltip>
                    </div>
                  </div>
                }
                key="action"
                render={(_: any, record: ProtocolInterface) => (
                  <div>
                    {formAction === FormAction.Edit && record.id === protocol.id ? (
                      <div className="generalStyles__flexCenter">
                        <div>
                          <Tooltip title={t("general.save")}>
                            <Button
                              className="buttonStyle__10"
                              disabled={!checkFormIsValid()}
                              onClick={onClickSave}
                              icon={<FontAwesomeIcon icon={faSave} />}
                            />
                          </Tooltip>
                        </div>
                        <div className="generalStyles__mlFix">
                          <Tooltip title={t("general.cancel")}>
                            <Button
                              className="buttonStyle__14"
                              onClick={resetForm}
                              icon={<FontAwesomeIcon icon={faX} />}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    ) : (
                      <ProtocolTableActions selectedItem={record} />
                    )}
                  </div>
                )}
              />
            </ColumnGroup>
          </Table>
        ) : (
          <>
            <div className="generalStyles__noAccessToListTable">
              <Empty
                description={t("organizationManagement.listNotAvailableOrganizations")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          </>
        )}
      </div>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostProtocol}
        isSuccess={isSuccessPostProtocol}
        requestType="POST"
        error={errorPostProtocol}
        name="Protocol"
      />
      <GlobalAlert2
        isError={isErrorPutProtocol}
        isSuccess={isSuccessPutProtocol}
        requestType="PUT"
        error={errorPutProtocol}
        name="Protocol"
      />
    </div>
  );
}

export default ProtocolTable;
